import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import departmentMajorApi, { F90Q } from '../../../services/departmentMajorApi';

type Mode = 'browse' | 'add' | 'edit';

export function useDepartmentMajorForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F90Q[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [f90q_01, setF90q01] = useState('');
  const [f90q_11, setF90q11] = useState('');
  const [f90q_91, setF90q91] = useState('');
  const [f90q_92, setF90q92] = useState<string | null>(null);
  const [f90q_93, setF90q93] = useState('');

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await departmentMajorApi.list(1, 500);
      setRecords(res.data || []);
      if ((res.data || []).length > 0) {
        setCurrentIdx(0);
      }
    } catch {
      message.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const syncFields = useCallback((rec: F90Q) => {
    setF90q01(rec.f90q_01 || '');
    setF90q11(rec.f90q_11 || '');
    setF90q91(rec.f90q_91 || '');
    setF90q92(rec.f90q_92);
    setF90q93(rec.f90q_93 || '');
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    if (records.length > 0 && currentIdx >= 0 && currentIdx < records.length) {
      syncFields(records[currentIdx]);
    }
  }, [records, currentIdx, syncFields]);

  // Navigation
  const navFirst = useCallback(() => setCurrentIdx(0), []);
  const navPrev = useCallback(() => setCurrentIdx((i) => Math.max(0, i - 1)), []);
  const navNext = useCallback(() => setCurrentIdx((i) => Math.min(records.length - 1, i + 1)), [records.length]);
  const navLast = useCallback(() => setCurrentIdx(records.length - 1), [records.length]);

  const startAdd = useCallback(() => {
    setMode('add');
    setF90q01('');
    setF90q11('');
    setF90q91('');
    setF90q92(null);
    setF90q93('');
  }, []);

  const startEdit = useCallback(() => {
    setMode('edit');
  }, []);

  const cancel = useCallback(() => {
    setMode('browse');
    if (records.length > 0) {
      syncFields(records[currentIdx]);
    }
  }, [records, currentIdx, syncFields]);

  const buildPayload = useCallback((): Partial<F90Q> => ({
    f90q_01: f90q_01.trim(),
    f90q_11: f90q_11,
    f90q_91: f90q_91,
    f90q_93: f90q_93,
  }), [f90q_01, f90q_11, f90q_91, f90q_93]);

  const save = useCallback(async () => {
    setLoading(true);
    try {
      const payload = buildPayload();
      if (mode === 'add') {
        await departmentMajorApi.create(payload);
        message.success('新增成功');
      } else {
        await departmentMajorApi.update(f90q_01, payload);
        message.success('儲存成功');
      }
      setMode('browse');
      await loadRecords();
    } catch (err: any) {
      if (err?.response?.status === 422) {
        const errors = err.response.data?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          message.error(errors[0].message);
        }
      } else {
        message.error('儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [mode, f90q_01, buildPayload, loadRecords]);

  const doDelete = useCallback(async () => {
    if (!f90q_01) return;
    setLoading(true);
    try {
      await departmentMajorApi.delete(f90q_01);
      message.success('刪除成功');
      await loadRecords();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [f90q_01, loadRecords]);

  const quickSearch = useCallback((keyword: string) => {
    const idx = records.findIndex((r) => r.f90q_01.toUpperCase().startsWith(keyword.toUpperCase()));
    if (idx >= 0) {
      setCurrentIdx(idx);
    } else {
      message.warning('查無資料');
    }
  }, [records]);

  return {
    mode, loading, records, currentIdx,
    f90q_01, f90q_11, f90q_91, f90q_92, f90q_93,
    setF90q01, setF90q11,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete,
    quickSearch,
    recordCount: records.length,
    recordPosition: records.length > 0 ? currentIdx + 1 : 0,
  };
}
